/* eslint-disable react/prop-types */
import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";

// example components
import { Stack } from "@mui/material";
import { Link } from "react-router-dom";
import SoftButton from "components/SoftButton";
import TableCommon from "components/TableCommon";

function DataTable({
  table,
  loading,
  syncing,
  filters,
  setFilters,
  accounts,
  accountsLoading,
  paginationData,
  setPaginationData,
  onFetchData,
  onSync,
  query,
  setQuery,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { setPageSize } = tableInstance;

  useEffect(() => {
    setPageSize(paginationData.size);
  }, [paginationData.size, setPageSize]);

  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    onFetchData(localFilters);
  };

  const handleReset = () => {
    const currentYear = String(new Date().getFullYear());
    const resetFilters = {
      year: currentYear,
      fromDate: "",
      toDate: "",
      accountCode: "",
      searchBy: "",
    };

    setLocalFilters(resetFilters);
    onFetchData(resetFilters);
  };

  return (
    <>
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        p={3}
        pb={1}
      >
        <SoftBox lineHeight={1} width="100%">
          <Stack spacing={2} direction="row" flexWrap="wrap">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Năm
              </SoftTypography>
              <SoftBox width="8rem">
                <SoftInput
                  placeholder="YYYY"
                  value={localFilters.year}
                  onChange={({ currentTarget }) => {
                    const next = currentTarget.value.replace(/\D/g, "").slice(0, 4);
                    setLocalFilters((prev) => ({ ...prev, year: next }));
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Từ ngày
              </SoftTypography>
              <SoftInput
                type="date"
                value={localFilters.fromDate}
                onChange={({ currentTarget }) =>
                  setLocalFilters((prev) => ({ ...prev, fromDate: currentTarget.value }))
                }
              />
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Đến ngày
              </SoftTypography>
              <SoftInput
                type="date"
                value={localFilters.toDate}
                onChange={({ currentTarget }) =>
                  setLocalFilters((prev) => ({ ...prev, toDate: currentTarget.value }))
                }
              />
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tài khoản ngân hàng
              </SoftTypography>
              <select
                value={localFilters.accountCode}
                onChange={(e) => setLocalFilters((prev) => ({ ...prev, accountCode: e.target.value }))}
                style={{ width: 280, height: 40, borderRadius: 8, border: "1px solid #d2d6da", padding: "0 8px" }}
              >
                <option value="">
                  {accountsLoading ? "Đang tải tài khoản..." : "Chọn tài khoản 112"}
                </option>
                {accounts.map((item) => (
                  <option key={item.accountCode} value={item.accountCode}>
                    {item.accountCode} - {item.accountName}
                  </option>
                ))}
              </select>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tìm kiếm
              </SoftTypography>
              <SoftBox width="16rem">
                <SoftInput
                  placeholder="Số CT, diễn giải, TK đối ứng"
                  value={localFilters.searchBy}
                  onChange={({ currentTarget }) =>
                    setLocalFilters((prev) => ({ ...prev, searchBy: currentTarget.value }))
                  }
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" alignItems="flex-end">
              <Stack direction="row" spacing={1}>
                <SoftBox
                  component="button"
                  onClick={handleApply}
                  disabled={loading || !localFilters.accountCode}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "none",
                    backgroundColor: "#1a73e8",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: loading || !localFilters.accountCode ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#1557b0" },
                  }}
                >
                  Tải dữ liệu
                </SoftBox>
                <SoftBox
                  component="button"
                  onClick={handleReset}
                  disabled={loading || syncing}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "1px solid #d2d6da",
                    backgroundColor: "white",
                    color: "#344767",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: loading || syncing ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#f8f9fa" },
                  }}
                >
                  Reset
                </SoftBox>
                <SoftBox
                  component="button"
                  onClick={onSync}
                  disabled={syncing || loading}
                  sx={{
                    height: "39px",
                    padding: "0 16px",
                    borderRadius: 0,
                    border: "none",
                    backgroundColor: syncing ? "#ccc" : "#4CAF50",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: "bold",
                    opacity: syncing || loading ? 0.5 : 1,
                    "&:hover": { backgroundColor: "#45a049" },
                  }}
                >
                  {syncing ? "Đang đồng bộ..." : "Đồng bộ dữ liệu"}
                </SoftBox>
              </Stack>
            </SoftBox>
          </Stack>
        </SoftBox>
      </SoftBox>

      <TableCommon
        loading={loading}
        query={query}
        setQuery={setQuery}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        table={table}
        isSorted={true}
        noEndBorder={false}
        pagination={{ variant: "gradient", color: "info" }}
        tableInstance={tableInstance}
      />
    </>
  );
}

DataTable.propTypes = {
  table: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  syncing: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  accounts: PropTypes.array.isRequired,
  accountsLoading: PropTypes.bool,
  paginationData: PropTypes.object.isRequired,
  setPaginationData: PropTypes.func.isRequired,
  onFetchData: PropTypes.func.isRequired,
  onSync: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  setQuery: PropTypes.func.isRequired,
};

export default DataTable;
